import chalk from 'chalk';
import ora from 'ora';
import { checkbox, confirm, input } from '@inquirer/prompts';
import {
  getDefaultSlackScopes,
  getScopesByCategory,
  SLACK_SCOPE_CATEGORY_LABELS,
  SLACK_SCOPE_PRESETS,
  generateSlackAppManifest,
  serializeManifestForSlackCli,
  createSlackApp,
  SlackApiError,
  type SlackScope,
  type SlackScopeCategory,
} from '@augmented/core';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, info, warn, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import { unwrapChannelConfig, type ChannelConfigRowEnvelope } from '../lib/channel-config.js';

/** The Slack payload as stored in `agent_channel_configs.config`. */
interface SlackChannelPayload {
  channel_type?: string;
  app_name?: string;
  scopes?: string[];
  bot_token_ref?: string;
  signing_secret_ref?: string;
  app_id?: string;
  /** Slack's workspace id - distinct from the row's tenant `team_id`. */
  team_id?: string;
}

import { writeFile } from 'node:fs/promises';
import { join } from 'node:path';
import { tmpdir } from 'node:os';

/**
 * `agt channels slack setup <agent-code-name>`
 */
export async function channelSlackSetupCommand(
  agentCodeName: string,
  options: {
    preset?: string;
    scopes?: string;
    skipCreate?: boolean;
    botToken?: string;
    signingSecret?: string;
    configToken?: string;
  },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Looking up agent "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    // Look up agent via API
    const agentData = await api.get<{
      agent: {
        agent_id: string;
        code_name: string;
        display_name: string;
        channels: string[];
      };
    }>(`/agents/${encodeURIComponent(agentCodeName)}`);

    const agent = agentData.agent;

    // Verify slack is in the agent's channel list
    const channels: string[] = agent.channels ?? [];
    if (!channels.includes('slack')) {
      spinner.fail(`Agent "${agentCodeName}" does not have Slack in its channel list.`);
      error('Enable Slack in the agent\'s channels first (add "slack" to the channels array).');
      process.exitCode = 1;
      return;
    }

    spinner.succeed(`Found agent "${agentCodeName}"`);

    // ── Scope selection ─────────────────────────────────────────────────
    let selectedScopes: SlackScope[];

    if (options.scopes) {
      selectedScopes = options.scopes.split(',').map((s) => s.trim()) as SlackScope[];
    } else if (options.preset) {
      const preset = options.preset as keyof typeof SLACK_SCOPE_PRESETS;
      if (!(preset in SLACK_SCOPE_PRESETS)) {
        error(`Unknown preset "${options.preset}". Available: minimal, standard, full`);
        process.exitCode = 1;
        return;
      }
      selectedScopes = [...SLACK_SCOPE_PRESETS[preset]];
      info(`Using "${preset}" preset (${selectedScopes.length} scopes)`);
    } else if (json) {
      selectedScopes = [...SLACK_SCOPE_PRESETS.standard];
    } else {
      const scopesByCategory = getScopesByCategory();
      const defaults = getDefaultSlackScopes();

      type CheckboxChoice = { name: string; value: SlackScope; checked: boolean };
      type CheckboxSeparator = { type: 'separator'; separator: string };
      const choices: Array<CheckboxChoice | CheckboxSeparator> = [];
      for (const [category, defs] of scopesByCategory) {
        const label = SLACK_SCOPE_CATEGORY_LABELS[category as SlackScopeCategory];
        choices.push({ type: 'separator', separator: `── ${label} ──` });
        for (const def of defs) {
          const riskColor =
            def.risk === 'high' ? chalk.red : def.risk === 'medium' ? chalk.yellow : chalk.green;
          choices.push({
            name: `${def.scope} ${chalk.dim(`— ${def.description}`)} ${riskColor(`[${def.risk}]`)}`,
            value: def.scope,
            checked: defaults.includes(def.scope),
          });
        }
      }

      selectedScopes = await checkbox({
        message: 'Select Slack bot scopes:',
        choices,
        pageSize: 30,
      });
    }

    if (selectedScopes.length === 0) {
      error('No scopes selected. At least one scope is required.');
      process.exitCode = 1;
      return;
    }

    info(`Selected ${selectedScopes.length} scope(s): ${selectedScopes.join(', ')}`);

    // ── Generate manifest ───────────────────────────────────────────────
    const manifest = generateSlackAppManifest({
      agent_name: agent.display_name ?? agentCodeName,
      description: `Augmented-managed Slack bot for agent ${agentCodeName}`,
      scopes: selectedScopes,
      // ENG-6044: per-agent slash commands carry the code-name suffix.
      // No-op until a slash_command_url is supplied, but keeps this
      // manifest consistent with the API-generated ones.
      agent_code_name: agent.code_name ?? agentCodeName,
    });

    const manifestObj = serializeManifestForSlackCli(manifest);

    const manifestJson = JSON.stringify(manifestObj, null, 2);
    const manifestPath = join(tmpdir(), `augmented-slack-manifest-${agentCodeName}.json`);
    await writeFile(manifestPath, manifestJson, 'utf-8');

    success(`Manifest written to ${manifestPath}`);
    console.log(chalk.dim(manifestJson));

    // ── Slack app creation via API ──────────────────────────────────────
    let appId: string | undefined;
    let botTokenRef: string | undefined = options.botToken;
    let signingSecretRef: string | undefined = options.signingSecret;

    if (!options.skipCreate) {
      let token = options.configToken;
      if (!token && !json) {
        token = (await input({
          message: 'Paste your Slack config token (xoxe-…) to create the app automatically, or leave empty to skip:',
        })).trim() || undefined;
      }

      if (token) {
        const createSpinner = ora({ text: 'Creating Slack app via API…', isSilent: json });
        createSpinner.start();

        try {
          const result = await createSlackApp(token, manifest);
          appId = result.app_id;
          signingSecretRef = `secret_ref://slack/${agentCodeName}/signing_secret`;
          createSpinner.succeed(`Slack app created: ${result.app_id}`);
          info(`OAuth URL: ${result.oauth_authorize_url}`);
        } catch (err) {
          createSpinner.fail('Failed to create Slack app via API.');
          if (err instanceof SlackApiError) {
            warn(`Slack API error: ${err.message}`);
          } else {
            warn((err as Error).message);
          }
          info(`Manifest saved at: ${manifestPath}`);
          info('Create the app manually via https://api.slack.com/apps and paste the manifest.');
        }
      } else {
        info(`Manifest saved at: ${manifestPath}`);
        info('Create the app manually via https://api.slack.com/apps and paste the manifest.');
      }
    }

    if (!signingSecretRef && !json) {
      const secret = await input({
        message: 'Paste your Slack Signing Secret, or leave empty to skip:',
      });
      if (secret.trim()) {
        signingSecretRef = `secret_ref://slack/${agentCodeName}/signing_secret`;
      }
    }

    if (!botTokenRef && !json) {
      const token = await input({
        message: 'Paste your Slack Bot Token (xoxb-…), or leave empty to skip:',
      });
      if (token.trim()) {
        botTokenRef = `secret_ref://slack/${agentCodeName}/bot_token`;
      }
    }

    // ── Store config via API ────────────────────────────────────────────
    const configSpinner = ora({ text: 'Saving channel config…', isSilent: json });
    configSpinner.start();

    const config = {
      channel_type: 'slack' as const,
      app_name: agent.display_name ?? agentCodeName,
      scopes: selectedScopes,
      ...(appId ? { app_id: appId } : {}),
      ...(botTokenRef ? { bot_token_ref: botTokenRef } : {}),
      ...(signingSecretRef ? { signing_secret_ref: signingSecretRef } : {}),
      manifest,
    };

    try {
      await api.put(
        `/agents/${encodeURIComponent(agentCodeName)}/channel-configs/slack`,
        {
          config,
          status: botTokenRef ? 'configured' : 'pending',
        },
      );
    } catch (err) {
      configSpinner.fail('Failed to save channel config.');
      error((err as Error).message);
      process.exitCode = 1;
      return;
    }

    configSpinner.succeed('Slack channel config saved.');

    if (json) {
      jsonOutput({
        ok: true,
        agent: agentCodeName,
        channel: 'slack',
        scopes: selectedScopes,
        status: botTokenRef ? 'configured' : 'pending',
        manifest_path: manifestPath,
      });
    } else {
      console.log();
      success(`Slack setup complete for "${agentCodeName}"`);
      info(`Status: ${botTokenRef ? 'configured' : 'pending (add credentials to activate)'}`);
    }
  } catch (err) {
    spinner.fail('Slack setup failed.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

/**
 * The single "this agent has no Slack config" answer, shared by the two ways of
 * finding that out: a 404 from the route, and a row whose payload is null.
 * Keeping them in one place is the point - they diverged once already.
 */
function reportSlackNotConfigured(agentCodeName: string, json: boolean): void {
  if (json) {
    jsonOutput({ ok: true, agent: agentCodeName, channel: 'slack', configured: false });
  } else {
    warn(`No Slack configuration found for "${agentCodeName}".`);
    info('Run `agt channels slack setup ' + agentCodeName + '` to configure.');
  }
}

/**
 * `agt channels slack status <agent-code-name>`
 */
export async function channelSlackStatusCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Fetching Slack config for "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    // ENG-8540: the payload is `data.config.config`, not `data.config`. Reading
    // one level short made every cell below print `-` - except Team ID, which
    // resolved to the row's TENANT team uuid and printed a real-looking wrong
    // value. See lib/channel-config.ts.
    const data = await api.get<ChannelConfigRowEnvelope<SlackChannelPayload>>(
      `/agents/${encodeURIComponent(agentCodeName)}/channel-configs/slack`,
    );

    spinner.stop();

    // ENG-8540 (review round 2): a row with a null payload is NOT configured.
    // `?? {}` reported `configured: true` alongside an empty table, which is the
    // same class of lie the shallow read produced - a real-looking answer over
    // absent data. lib/channel-config.ts defines a null payload as not
    // configured; this is the 404 path's answer, reached without a 404.
    const { payload: config } = unwrapChannelConfig(data);
    if (config === null) {
      reportSlackNotConfigured(agentCodeName, json);
      return;
    }

    if (json) {
      jsonOutput({
        ok: true,
        agent: agentCodeName,
        channel: 'slack',
        configured: true,
        config,
      });
      return;
    }

    console.log(chalk.bold(`\nSlack Configuration: ${agentCodeName}\n`));

    const rows: string[][] = [
      ['App Name', config.app_name ?? '-'],
      ['App ID', config.app_id ?? '-'],
      ['Team ID', config.team_id ?? '-'],
      ['Bot Token', config.bot_token_ref ? chalk.green('configured') : chalk.dim('not set')],
      ['Signing Secret', config.signing_secret_ref ? chalk.green('configured') : chalk.dim('not set')],
      ['Scopes', (config.scopes ?? []).join(', ') || '-'],
    ];

    table(['Field', 'Value'], rows);
  } catch (err) {
    spinner.stop();
    if ((err as any).status === 404) {
      reportSlackNotConfigured(agentCodeName, json);
      return;
    }

    spinner.fail('Failed to fetch Slack status.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

/**
 * `agt channels slack remove <agent-code-name>`
 */
export async function channelSlackRemoveCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Looking up agent "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    // Check if config exists
    let config: { app_id?: string; app_name?: string } | null = null;
    try {
      const data = await api.get<ChannelConfigRowEnvelope<{ app_id?: string; app_name?: string }>>(
        `/agents/${encodeURIComponent(agentCodeName)}/channel-configs/slack`,
      );
      config = unwrapChannelConfig(data).payload;
    } catch (err) {
      if ((err as any).status === 404) {
        spinner.info(`No Slack configuration found for "${agentCodeName}".`);
        if (json) {
          jsonOutput({ ok: true, agent: agentCodeName, channel: 'slack', removed: false, reason: 'not_configured' });
        }
        return;
      }
      throw err;
    }

    spinner.stop();

    // Confirm removal
    if (!json) {
      const appLabel = config?.app_id
        ? ` (App ID: ${config.app_id})`
        : config?.app_name
          ? ` (${config.app_name})`
          : '';
      const confirmed = await confirm({
        message: `Remove Slack bot configuration${appLabel} for "${agentCodeName}"?`,
        default: false,
      });

      if (!confirmed) {
        warn('Aborted.');
        return;
      }
    }

    const removeSpinner = ora({ text: 'Removing Slack configuration…', isSilent: json });
    removeSpinner.start();

    await api.del(`/agents/${encodeURIComponent(agentCodeName)}/channel-configs/slack`);

    removeSpinner.succeed('Slack configuration removed.');

    if (json) {
      jsonOutput({ ok: true, agent: agentCodeName, channel: 'slack', removed: true });
    } else {
      if (config?.app_id) {
        console.log();
        info(`Note: The Slack app (${config.app_id}) still exists in your Slack workspace.`);
        info('Delete it manually at https://api.slack.com/apps if no longer needed.');
      }
    }
  } catch (err) {
    error((err as Error).message);
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    }
    process.exitCode = 1;
  }
}
